/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** What a seat of each plan unlocks, side by side. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, UnstyledButton } from '@/shared/ui';
import { MARK, TIER_ACCENT, resolveTier } from '@/features/onboarding/components/sections/PlanChoice.catalog';
import type { SeatUnlock } from './seat-unlocks';

/** The tier's own accent — an emerald is green, whatever its position. */
const toneOf = (planId: string): string => TIER_ACCENT[resolveTier(planId)];

/**
 * One plan's card.
 *
 * The big number is interactions, because it is the allowance people
 * actually run out of. The multiple beside it is what makes it legible:
 * "800" means nothing to somebody who has not memorised the entry tier.
 */
/** The check beside an included feature, as the plan cards already draw it. */
const Tick: React.FC = () => (
  <svg
    width={14} height={14} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
    style={{ flex: 'none', marginTop: 2, color: 'var(--plan-feature-on)' }}
  >
    <path fillRule="evenodd" d={MARK.yes} />
  </svg>
);

/**
 * What the plan includes, ticked.
 *
 * Only what it has. The three cards side by side are the comparison; a cross
 * inside one of them would argue against the thing the card is offering.
 *
 * @param props - The feature row ids this plan carries.
 * @returns The list, or nothing when the plan carries none.
 */
const Included: React.FC<{ rows: string[] }> = ({ rows }) => {
  const { t } = useTranslation('@pages/onboarding');
  if (!rows.length) return null;
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)' }}
    >
      {rows.map((row) => (
        <div
          key={row}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}
        >
          <Tick />
          <Text
            component="span"
            style={{
              fontSize: 'var(--text-tag)', color: 'var(--text-primary)',
              lineHeight: 1.35, textWrap: 'pretty',
            }}
          >
            {t(`planChoice.matrix.${row}`)}
          </Text>
        </div>
      ))}
    </div>
  );
};

const UnlockCard: React.FC<{
  unlock: SeatUnlock;
  index: number;
  top: boolean;
  solo: boolean;
  current: boolean;
  onAdd: () => void;
}> = ({ unlock, index, top, solo, current, onAdd }) => {
  const { t } = useTranslation('features/subscription');
  const Glyph = unlock.icon as React.FC<{ width?: number; height?: number }> | undefined;
  return (
    <div
      data-testid={`plan-ladder-${unlock.planId}`}
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
        padding: 'var(--space-5)', borderRadius: 'var(--radius-xl)',
        background: top ? 'var(--surface-primary)' : 'var(--surface-raised)',
        border: top
          ? '1.5px solid var(--seed-base)'
          : '1px solid var(--border-subtle)',
        boxShadow: top ? 'var(--shadow-md)' : 'none',
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        {Glyph ? (
          <Box
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flex: 'none', color: toneOf(unlock.planId),
            }}
          >
            <Glyph width={18} height={18} />
          </Box>
        ) : (
          <Box
            style={{
              width: 10, height: 10, borderRadius: 'var(--radius-full)',
              background: toneOf(unlock.planId), flex: 'none',
            }}
          />
        )}
        <Text
          component="span"
          style={{
            fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
          }}
        >
          {unlock.title}
        </Text>
        {top && (
          <Text
            component="span"
            style={{
              padding: '2px var(--space-2)', borderRadius: 'var(--radius-full)',
              background: 'var(--brand-beige)', color: 'var(--seed-base)',
              fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)',
            }}
          >
            {t('v2.ladder.mostComplete')}
          </Text>
        )}
      </div>
      {/* Rendered for every card, not only the current one: skipping it on
          the others left solo cards one row shorter at the top than the
          current plan's, so the price and feature rows below never lined
          up across the three cards. An invisible placeholder keeps the row
          -- and everything under it -- at the same height as its siblings. */}
      <Text
        component="span"
        aria-hidden={solo && !current ? true : undefined}
        style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)' }}
      >
        {solo
          ? (current ? t('v2.ladder.currentPlan') : ' ')
          : t('v2.ladder.seatsOnTeam', { count: unlock.seatsOnTeam })}
      </Text>

      {/* What a seat on this tier brings beyond the quotas. Stated on the
          card rather than once above it, because the figure is what differs
          between the tiers the ladder is asking the reader to compare. */}
      {!solo && unlock.supportPerSeat > 0 ? (
        <Text
          component="span"
          data-testid={`ladder-support-${unlock.planId}`}
          style={{ fontSize: 'var(--text-meta)', color: 'var(--status-success-text)' }}
        >
          {t('v2.ladder.supportPerSeat', { count: unlock.supportPerSeat })}
        </Text>
      ) : null}

      <div
        style={{
          display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)',
          marginTop: 'var(--space-2)',
        }}
      >
        <Text
          component="span"
          style={{
            fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
            letterSpacing: '-0.02em',
          }}
        >
          {unlock.interactions ?? '—'}
        </Text>
        {unlock.timesEntry && (
          <Text
            component="span"
            style={{
              fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
              color: 'var(--status-success-text)',
            }}
          >
            {t('v2.ladder.timesMore', { times: unlock.timesEntry })}
          </Text>
        )}
      </div>
      <Text
        component="span"
        style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)' }}
      >
        {t('v2.ladder.interactionsPerMonth')}
      </Text>

      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          gap: 'var(--space-3)', marginTop: 'var(--space-2)',
          paddingTop: 'var(--space-2)',
          borderTop: '1px solid var(--border-ultra-subtle)',
        }}
      >
        <Text
          component="span"
          style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}
        >
          {t('v2.ladder.searchesPerMonth')}
        </Text>
        <Text
          component="span"
          style={{
            fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
            color: 'var(--text-primary)',
          }}
        >
          {unlock.searches ?? '—'}
        </Text>
      </div>

      <Included rows={unlock.features} />

      {/* An announced tier is on the card for reading -- the agency's seats
          sit on it -- but there is nothing to click: Payien refuses to bill
          a plan the catalogue lists as inactive. */}
      {!unlock.sellable && (
        <Text
          component="span"
          data-testid={`plan-ladder-unavailable-${unlock.planId}`}
          style={{
            marginTop: 'var(--space-2)', alignSelf: 'flex-start',
            fontSize: 'var(--text-small)', color: 'var(--text-secondary)',
          }}
        >
          {t('v2.ladder.unavailable')}
        </Text>
      )}

      {unlock.sellable && !(solo && current) && (
        <UnstyledButton
          onClick={onAdd}
          data-testid={`plan-ladder-add-${unlock.planId}`}
          style={{
            marginTop: 'var(--space-2)', alignSelf: 'flex-start',
            background: 'none', cursor: 'pointer', fontSize: 'var(--text-small)',
            fontWeight: 'var(--font-bold)', color: 'var(--seed-base)',
          }}
        >
          {solo
            ? t('v2.ladder.changeTo', { plan: unlock.title })
            : t('v2.ladder.addSeat', { plan: unlock.title })}
        </UnstyledButton>
      )}
    </div>
  );
};

/**
 * The ladder.
 *
 * @param props - The plans, and where to send somebody who wants one.
 * @returns The cards.
 */
export const SeatUnlocks: React.FC<{
  unlocks: SeatUnlock[];
  onAdd: (planId: string) => void;
  /** Solo broker: one fixed seat, so the cards offer a tier change instead. */
  solo?: boolean;
  /** The tier the solo broker's seat is on, in the ladder's own spelling. */
  currentPlanId?: string | null;
}> = ({ unlocks, onAdd, solo = false, currentPlanId = null }) => {
  const { t } = useTranslation('features/subscription');
  if (!unlocks.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Text
        component="h3"
        style={{
          margin: 0, fontFamily: 'var(--font-family-alt-2)',
          fontSize: 'var(--text-app-section)', fontWeight: 'var(--font-bold)',
          color: 'var(--text-primary)',
        }}
      >
        {t(solo ? 'v2.ladder.titleSolo' : 'v2.ladder.title')}
      </Text>
      <div
        style={{
          display: 'grid', gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}
      >
        {unlocks.map((unlock, index) => (
          <UnlockCard
            key={unlock.planId}
            unlock={unlock}
            index={index}
            top={index === unlocks.length - 1}
            solo={solo}
            current={solo && unlock.planId === currentPlanId}
            onAdd={() => onAdd(unlock.planId)}
          />
        ))}
      </div>
    </div>
  );
};
